<template>
  <div class="pend-section">
    <!-- Header bar -->
    <div class="pend-header">
      <h2 class="pend-title">Pendências</h2>
      <button v-if="accessMode === 'edit'" class="btn btn-primary pend-new-btn" type="button" @click="openCreateForm">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Nova pendência
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="pend-empty">Carregando…</div>

    <!-- Empty state -->
    <div v-else-if="items.length === 0" class="pend-empty">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
      <p>Nenhuma pendência registrada.</p>
      <button v-if="accessMode === 'edit'" class="btn btn-primary" type="button" @click="openCreateForm">Registrar pendência</button>
    </div>

    <!-- List -->
    <div v-else class="pend-list">
      <div v-for="item in items" :key="item.id" class="pend-card">
        <div class="pend-card-top">
          <div class="pend-card-info">
            <span class="pend-short">{{ item.shortDescription }}</span>
            <span class="pend-meta">{{ item.responsavel }} · {{ formatDate(item.createdAt) }}</span>
          </div>
          <div class="pend-card-badges">
            <span v-if="item.taskId" class="pend-badge pend-badge--task" :title="item.taskTitle">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
              {{ item.taskTitle || 'Atividade' }}
            </span>
          </div>
          <div class="pend-card-actions">
            <button class="pend-action-btn" type="button" title="Ver detalhes" @click="openView(item)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              Ver
            </button>
            <button v-if="accessMode === 'edit'" class="pend-action-btn" type="button" title="Editar" @click="openEdit(item)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Editar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── VIEW MODAL ── -->
    <Teleport to="body">
      <div v-if="viewItem" class="pend-overlay" @click.self="viewItem = null">
        <div class="pend-modal">
          <div class="pend-modal-header">
            <h3 class="pend-modal-title">{{ viewItem.shortDescription }}</h3>
            <button class="pend-modal-close" type="button" @click="viewItem = null">✕</button>
          </div>
          <div class="pend-modal-body">
            <div class="pend-detail-row">
              <span class="pend-detail-label">Responsável</span>
              <span class="pend-detail-val">{{ viewItem.responsavel || '—' }}</span>
            </div>
            <div class="pend-detail-row">
              <span class="pend-detail-label">Registrado por</span>
              <span class="pend-detail-val">{{ viewItem.createdBy }} · {{ formatDate(viewItem.createdAt) }}</span>
            </div>
            <div v-if="viewItem.taskId" class="pend-detail-row">
              <span class="pend-detail-label">Atividade vinculada</span>
              <span class="pend-detail-val pend-task-link">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
                {{ viewItem.taskTitle || viewItem.taskId }}
              </span>
            </div>
            <div v-if="viewItem.fullDescription" class="pend-detail-full">
              <span class="pend-detail-label">Descrição completa</span>
              <p class="pend-full-text">{{ viewItem.fullDescription }}</p>
            </div>
            <div v-if="viewItem.photo" class="pend-detail-photo">
              <span class="pend-detail-label">Foto</span>
              <img :src="viewItem.photo" class="pend-photo-img" alt="Foto da pendência" />
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ── CREATE / EDIT MODAL ── -->
    <Teleport to="body">
      <div v-if="showForm" class="pend-overlay" @click.self="closeForm">
        <div class="pend-modal pend-modal--form">
          <div class="pend-modal-header">
            <h3 class="pend-modal-title">{{ editingId ? 'Editar pendência' : 'Nova pendência' }}</h3>
            <button class="pend-modal-close" type="button" @click="closeForm">✕</button>
          </div>
          <div class="pend-modal-body">
            <!-- Responsável: user selector -->
            <div class="pend-form-field">
              <label class="pend-form-label">Responsável</label>
              <select v-model="form.responsavel" class="pend-form-input">
                <option value="">Selecione o responsável</option>
                <option v-for="u in userOptions" :key="u.uid" :value="u.name">{{ u.name }}</option>
              </select>
            </div>

            <div class="pend-form-field">
              <label class="pend-form-label">Descrição curta <span class="pend-required">*</span></label>
              <input v-model="form.shortDescription" class="pend-form-input" type="text" placeholder="Resumo da pendência" />
            </div>
            <div class="pend-form-field">
              <label class="pend-form-label">Descrição completa</label>
              <textarea v-model="form.fullDescription" class="pend-form-input pend-form-textarea" rows="4" placeholder="Detalhes, contexto, ações necessárias…"></textarea>
            </div>
            <div class="pend-form-field">
              <label class="pend-form-label">Atividade (opcional)</label>
              <select v-model="form.taskId" class="pend-form-input" @change="onTaskChange">
                <option value="">Nenhuma atividade</option>
                <option v-for="t in tasks" :key="t.id" :value="t.id">{{ t.title }}</option>
              </select>
            </div>

            <!-- Photo: image-grid style -->
            <div class="pend-form-field">
              <label class="pend-form-label">Foto (opcional)</label>
              <div
                class="pend-image-grid"
                :class="{ 'is-drag-over': photoDragOver }"
                @dragenter.prevent="photoDragOver = true"
                @dragover.prevent="photoDragOver = true"
                @dragleave.prevent="photoDragOver = false"
                @drop.prevent="onPhotoDrop"
              >
                <!-- Preview -->
                <div v-if="form.photo" class="pend-image-preview">
                  <img :src="form.photo" alt="preview" />
                  <button class="pend-image-remove" type="button" @click="form.photo = ''" aria-label="Remover foto">
                    <span class="pend-trash-icon"></span>
                  </button>
                </div>
                <!-- Add button (only when no photo yet) -->
                <button v-if="!form.photo" class="pend-image-add" type="button" @click="triggerPhotoInput">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 4v10M4 9h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                </button>
              </div>
              <input ref="photoInputRef" type="file" accept="image/*" class="pend-hidden-input" @change="onFileChange" />
            </div>

            <p v-if="formError" class="pend-form-error">{{ formError }}</p>
          </div>
          <div class="pend-modal-footer">
            <button v-if="editingId" class="btn btn-danger" type="button" :disabled="saving" @click="deletePendencia">Apagar</button>
            <div class="pend-modal-footer-right">
              <button class="btn btn-ghost" type="button" @click="closeForm">Cancelar</button>
              <button class="btn btn-primary" type="button" :disabled="saving" @click="submitForm">
                {{ saving ? 'Salvando…' : (editingId ? 'Salvar' : 'Registrar') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch, type PropType } from 'vue'
import { pendenciasStore, type Pendencia } from '@/store/pendencias'
import { accountsStore } from '@/store/accounts'
import { uiStore } from '@/store/ui'

interface TaskOption {
  id: string
  title: string
}

export default defineComponent({
  name: 'ProjectPendenciasSection',
  props: {
    projectId: { type: String, required: true },
    projectName: { type: String, default: '' },
    accessMode: { type: String as PropType<'read' | 'edit'>, default: 'read' },
    tasks: { type: Array as PropType<TaskOption[]>, default: () => [] },
    openCreate: { type: Boolean, default: false }
  },
  setup(props) {
    const items = computed(() => pendenciasStore.state.items)
    const loading = computed(() => pendenciasStore.state.loading)

    const userOptions = computed(() =>
      accountsStore.all().filter(u => u.name).sort((a, b) => a.name.localeCompare(b.name))
    )

    const viewItem = ref<Pendencia | null>(null)
    const showForm = ref(false)
    const editingId = ref('')
    const saving = ref(false)
    const formError = ref('')
    const photoDragOver = ref(false)
    const photoInputRef = ref<HTMLInputElement | null>(null)

    const emptyForm = () => ({
      responsavel: '',
      shortDescription: '',
      fullDescription: '',
      photo: '',
      taskId: '',
      taskTitle: ''
    })

    const form = ref(emptyForm())

    const openCreateForm = () => {
      editingId.value = ''
      form.value = emptyForm()
      formError.value = ''
      showForm.value = true
    }

    const openView = (item: Pendencia) => {
      viewItem.value = item
    }

    const openEdit = (item: Pendencia) => {
      editingId.value = item.id
      form.value = {
        responsavel: item.responsavel,
        shortDescription: item.shortDescription,
        fullDescription: item.fullDescription,
        photo: item.photo,
        taskId: item.taskId,
        taskTitle: item.taskTitle
      }
      formError.value = ''
      showForm.value = true
    }

    const closeForm = () => {
      showForm.value = false
      editingId.value = ''
      form.value = emptyForm()
      photoDragOver.value = false
    }

    const onTaskChange = () => {
      const task = props.tasks.find(t => t.id === form.value.taskId)
      form.value.taskTitle = task?.title || ''
    }

    const triggerPhotoInput = () => {
      photoInputRef.value?.click()
    }

    const loadFile = (file: File) => {
      const reader = new FileReader()
      reader.onload = (ev) => { form.value.photo = ev.target?.result as string }
      reader.readAsDataURL(file)
    }

    const onFileChange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) loadFile(file)
      if (photoInputRef.value) photoInputRef.value.value = ''
    }

    const onPhotoDrop = (e: DragEvent) => {
      photoDragOver.value = false
      const file = e.dataTransfer?.files?.[0]
      if (file && file.type.startsWith('image/')) loadFile(file)
    }

    const submitForm = async () => {
      if (!form.value.shortDescription.trim()) {
        formError.value = 'Descrição curta é obrigatória.'
        return
      }
      saving.value = true
      formError.value = ''
      try {
        if (editingId.value) {
          await pendenciasStore.update(props.projectId, editingId.value, form.value)
          uiStore.toast('Pendência atualizada', 'success')
        } else {
          await pendenciasStore.create(props.projectId, { ...form.value, projectName: props.projectName })
          uiStore.toast('Pendência registrada', 'success')
        }
        closeForm()
      } catch (err) {
        formError.value = `${err}`
      } finally {
        saving.value = false
      }
    }

    const deletePendencia = async () => {
      if (!editingId.value) return
      if (!await uiStore.confirm('Apagar esta pendência?')) return
      try {
        await pendenciasStore.remove(props.projectId, editingId.value)
        uiStore.toast('Pendência apagada', 'success')
        closeForm()
      } catch (err) {
        uiStore.toast(`${err}`, 'error')
      }
    }

    const formatDate = (iso: string) => {
      if (!iso) return ''
      const d = new Date(iso)
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
    }

    watch(() => props.openCreate, (val) => {
      if (val) openCreateForm()
    })

    onMounted(async () => {
      pendenciasStore.load(props.projectId)
      if (accountsStore.all().length === 0) {
        await accountsStore.refresh().catch((_err) => { /* ignore */ })
      }
    })

    watch(() => props.projectId, (id) => {
      if (id) pendenciasStore.load(id)
    })

    return {
      items, loading, userOptions, viewItem, showForm, editingId, saving, formError, form,
      photoDragOver, photoInputRef,
      openCreateForm, openView, openEdit, closeForm, onTaskChange,
      triggerPhotoInput, onFileChange, onPhotoDrop,
      submitForm, deletePendencia, formatDate
    }
  }
})
</script>

<style scoped lang="scss">
.pend-section {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-height: 0;
}

.pend-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pend-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--ink);
}

.pend-new-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 8px 16px;
}

.pend-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 20px;
  text-align: center;
  color: var(--ink-muted);
  font-size: 14px;

  svg { opacity: 0.3; }
  p { margin: 0; }
}

/* ── LIST ── */
.pend-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pend-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 14px 16px;
  transition: box-shadow 0.12s;

  &:hover { box-shadow: var(--shadow-sm); }
}

.pend-card-top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.pend-card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.pend-short {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pend-meta {
  font-size: 12px;
  color: var(--ink-muted);
}

.pend-card-badges {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}

.pend-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;

  &--task {
    background: #dcfce7;
    color: #15803d;
    max-width: 160px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.pend-card-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.pend-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-muted);
  padding: 5px 9px;
  border-radius: 6px;
  transition: background 0.1s, color 0.1s;

  &:hover { background: rgba(31,30,26,0.06); color: var(--ink); }
}

/* ── OVERLAY & MODAL ── */
.pend-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.4);
  display: grid;
  place-items: center;
  padding: 20px;
}

.pend-modal {
  width: min(560px, 100%);
  background: var(--surface, #fff);
  border-radius: 18px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.22);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 40px);
  overflow: hidden;

  &--form {
    width: min(620px, 100%);
  }
}

.pend-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.pend-modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
}

.pend-modal-close {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: var(--ink-muted);
  padding: 4px 8px;
  border-radius: 6px;
  &:hover { background: var(--card); }
}

.pend-modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pend-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 8px;
}

.pend-modal-footer-right {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

/* ── VIEW DETAILS ── */
.pend-detail-row {
  display: flex;
  gap: 12px;
  align-items: baseline;
}

.pend-detail-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-muted);
  flex-shrink: 0;
  min-width: 130px;
}

.pend-detail-val {
  font-size: 14px;
  color: var(--ink);
}

.pend-task-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #15803d;
  font-weight: 600;
}

.pend-detail-full {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pend-full-text {
  margin: 0;
  font-size: 14px;
  color: var(--ink);
  line-height: 1.6;
  white-space: pre-wrap;
}

.pend-detail-photo {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pend-photo-img {
  max-width: 100%;
  max-height: 380px;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid var(--border);
}

/* ── FORM ── */
.pend-form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pend-form-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.pend-required { color: #dc2626; }

.pend-form-input {
  border: 1.5px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 9px 12px;
  font-size: 14px;
  background: var(--bg);
  color: var(--ink);
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.14s;

  &:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-light); }
}

.pend-form-textarea {
  resize: vertical;
  min-height: 100px;
}

/* ── IMAGE GRID (photo upload) ── */
.pend-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  padding: 8px;
  border: 1.5px dashed var(--border-strong, #ccc);
  border-radius: var(--radius);
  min-height: 60px;
  transition: border-color 0.2s, background 0.2s;

  &.is-drag-over {
    border-color: var(--accent, #2563eb);
    background: rgba(37, 99, 235, 0.04);
  }
}

.pend-image-preview {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(31, 30, 26, 0.1);
  aspect-ratio: 1 / 1;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: filter 0.22s, transform 0.22s;
  }

  &:hover img {
    filter: grayscale(100%) brightness(0.7);
    transform: scale(1.04);
  }

  &:hover .pend-image-remove { opacity: 1; }
}

.pend-image-remove {
  position: absolute;
  inset: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.2s;
}

.pend-trash-icon {
  width: 28px;
  height: 28px;
  background: #fff;
  mask-image: url('@/assets/trash.svg');
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  -webkit-mask-image: url('@/assets/trash.svg');
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
}

.pend-image-add {
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  border: 1.5px dashed rgba(37, 99, 235, 0.3);
  background: rgba(37, 99, 235, 0.04);
  color: var(--accent, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.14s, border-color 0.14s;
  min-height: 60px;

  &:hover {
    background: rgba(37, 99, 235, 0.09);
    border-color: var(--accent, #2563eb);
  }
}

.pend-hidden-input {
  display: none;
}

.pend-form-error {
  font-size: 13px;
  color: #dc2626;
  margin: 0;
  font-weight: 600;
}
</style>
